import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { TwoFactorCodeInput } from '@/components/auth/TwoFactorCodeInput';
import { QRCodeDisplay } from '@/components/auth/QRCodeDisplay';
import { useAuth } from '@/lib/auth';
import { returnPathFrom } from '@/lib/return-to';
import { ShieldPlus, Loader2 } from 'lucide-react';
import { REMEMBER_2FA_TOKEN_KEY } from './TwoFactorVerifyPage';

interface LocationState {
  setupToken?: string;
  /** FR-001f: the page to open once signed in, passed on by the login form. */
  from?: string;
  profile?: {
    email: string;
    firstname?: string;
    lastname?: string;
  };
}

interface SetupDataResponse {
  secret: string;
  qrCodeUrl: string;
}

interface TwoFAEnableResponse {
  token: string;
  user: {
    id: number;
    email: string;
    name: string;
    roles: string[];
  };
  remember2faToken?: string;
}

interface ErrorResponse {
  error: string;
  message: string;
}

export function TwoFactorSetupPage(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const state = location.state as LocationState | null;
  const setupToken = state?.setupToken;
  const profile = state?.profile;
  const returnTo = returnPathFrom(state);

  const [setupData, setSetupData] = useState<SetupDataResponse | null>(null);
  const [code, setCode] = useState('');
  const [rememberDevice, setRememberDevice] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isLoadingSetup, setIsLoadingSetup] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch setup data on mount
  useEffect(() => {
    if (!setupToken) return;

    const fetchSetupData = async (): Promise<void> => {
      try {
        const response = await fetch('/api/v1/auth/2fa/setup', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ pendingToken: setupToken }),
        });

        if (!response.ok) {
          const errorData = await response.json() as ErrorResponse;
          throw new Error(errorData.message || 'Failed to load setup data');
        }

        const data = await response.json() as SetupDataResponse;
        setSetupData(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load setup data');
      } finally {
        setIsLoadingSetup(false);
      }
    };

    void fetchSetupData();
  }, [setupToken]);

  const handleSubmit = useCallback(async (e: React.SyntheticEvent): Promise<void> => {
    e.preventDefault();

    if (!setupData) {
      setError('Setup data not loaded');
      return;
    }

    if (code.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      const response = await fetch('/api/v1/auth/2fa/enable', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          pendingToken: setupToken,
          secret: setupData.secret,
          code,
          rememberDevice,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json() as ErrorResponse;
        throw new Error(errorData.message || 'Verification failed');
      }

      const data = await response.json() as TwoFAEnableResponse;

      // Store remember token if provided
      if (data.remember2faToken) {
        try {
          localStorage.setItem(REMEMBER_2FA_TOKEN_KEY, data.remember2faToken);
        } catch {
          // Ignore storage errors
        }
      }

      // Complete login
      login(data.token);
      void navigate(returnTo, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
      setCode(''); // Clear code on error for retry
    } finally {
      setIsSubmitting(false);
    }
  }, [code, setupToken, setupData, rememberDevice, login, navigate, returnTo]);

  const handleCodeChange = useCallback((newCode: string): void => {
    setCode(newCode);
    setError(null);
  }, []);

  // Redirect if no setup token
  if (!setupToken) {
    return <Navigate to="/login" replace state={{ from: returnTo }} />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <Card className="w-full max-w-lg" data-testid="2fa-setup-form">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <ShieldPlus className="w-8 h-8 text-primary" />
            </div>
          </div>
          <CardTitle className="text-2xl">Set Up Two-Factor Authentication</CardTitle>
          <CardDescription>
            {profile?.email ? (
              <>Secure your account <strong>{profile.email}</strong> with 2FA</>
            ) : (
              <>Add an extra layer of security to your account</>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoadingSetup ? (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-muted-foreground">Loading setup data...</p>
            </div>
          ) : error && !setupData ? (
            <div className="space-y-4">
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => { void navigate('/login', { replace: true, state: { from: returnTo } }); }}
              >
                Return to login
              </Button>
            </div>
          ) : setupData ? (
            <form onSubmit={(e) => { void handleSubmit(e); }} className="space-y-6">
              {/* Step 1: QR Code */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium">
                    1
                  </div>
                  <h3 className="font-medium">Scan with your authenticator app</h3>
                </div>
                <QRCodeDisplay
                  qrCodeUrl={setupData.qrCodeUrl}
                  secret={setupData.secret}
                />
              </div>

              {/* Step 2: Enter Code */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium">
                    2
                  </div>
                  <h3 className="font-medium">Enter the code from your app</h3>
                </div>

                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <TwoFactorCodeInput
                  value={code}
                  onChange={handleCodeChange}
                  disabled={isSubmitting}
                  autoFocus={false}
                  error={!!error}
                />
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="rememberDevice"
                  checked={rememberDevice}
                  onCheckedChange={(checked: boolean | 'indeterminate') => { setRememberDevice(checked === true); }}
                  disabled={isSubmitting}
                />
                <Label
                  htmlFor="rememberDevice"
                  className="text-sm text-muted-foreground cursor-pointer"
                >
                  Remember this device for 14 days
                </Label>
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={isSubmitting || code.length !== 6}
                data-testid="2fa-setup-submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Enabling 2FA...
                  </>
                ) : (
                  'Enable Two-Factor Authentication'
                )}
              </Button>

              <div className="text-center">
                <Button
                  type="button"
                  variant="link"
                  className="text-sm text-muted-foreground"
                  onClick={() => { void navigate('/login', { replace: true, state: { from: returnTo } }); }}
                  disabled={isSubmitting}
                >
                  Use a different account
                </Button>
              </div>
            </form>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
